import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

const GuestSelector = ({ rooms, onRoomsChange }) => {
  const [showPopup, setShowPopup] = useState(false);
  const guestInputRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 380 });

  // Resolve theme dynamically based on current URL path
  const getTheme = () => {
    const path = window.location.pathname;
    if (path.includes('/admin')) {
      return {
        focusRing: 'focus:ring-red-500 focus:border-red-500',
        textAccent: 'text-red-600',
        badgeBg: 'bg-red-50 text-red-700 border-red-200',
        roomBadge: 'bg-red-50 text-red-700 border-red-200',
        cardBg: 'bg-slate-50/90 border-slate-200',
        selectFocus: 'focus:ring-red-500 focus:border-red-500',
        btnBg: 'from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        stepperHover: 'hover:bg-red-50 hover:border-red-500 hover:text-red-600',
        addBtn: 'border-red-300 hover:border-red-500 bg-red-50/40 hover:bg-red-50 text-red-600'
      };
    } else if (path.includes('/support')) {
      return {
        focusRing: 'focus:ring-green-500 focus:border-green-500',
        textAccent: 'text-green-600',
        badgeBg: 'bg-green-50 text-green-700 border-green-200',
        roomBadge: 'bg-green-50 text-green-700 border-green-200',
        cardBg: 'bg-slate-50/90 border-slate-200',
        selectFocus: 'focus:ring-green-500 focus:border-green-500',
        btnBg: 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        stepperHover: 'hover:bg-green-50 hover:border-green-500 hover:text-green-600',
        addBtn: 'border-green-300 hover:border-green-500 bg-green-50/40 hover:bg-green-50 text-green-600'
      };
    }
    return {
      focusRing: 'focus:ring-blue-500 focus:border-blue-500',
      textAccent: 'text-blue-600',
      badgeBg: 'bg-blue-50 text-blue-700 border-blue-200',
      roomBadge: 'bg-blue-50 text-blue-700 border-blue-200',
      cardBg: 'bg-slate-50/90 border-slate-200',
      selectFocus: 'focus:ring-blue-500 focus:border-blue-500',
      btnBg: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
      stepperHover: 'hover:bg-blue-50 hover:border-blue-500 hover:text-blue-600',
      addBtn: 'border-blue-300 hover:border-blue-500 bg-blue-50/40 hover:bg-blue-50 text-blue-600'
    };
  };

  const theme = getTheme();

  // Calculate precise fixed viewport coordinates for Portal
  const updatePopupPosition = useCallback(() => {
    if (!guestInputRef.current) return;
    const rect = guestInputRef.current.getBoundingClientRect();
    const width = Math.min(380, window.innerWidth - 24);
    
    // Right-align with the trigger input so it opens naturally below the input
    let left = rect.right - width;
    
    // Clamp to viewport
    if (left + width > window.innerWidth - 12) {
      left = window.innerWidth - width - 12;
    }
    if (left < 12) {
      left = 12;
    }
    
    let top = rect.bottom + 8;
    if (top + 420 > window.innerHeight && rect.top > 420) {
      top = Math.max(12, rect.top - 420 - 8);
    }
    
    setPopupPos({ top, left, width });
  }, []);

  useEffect(() => {
    if (showPopup) {
      updatePopupPosition();
      
      window.addEventListener('scroll', updatePopupPosition, { passive: true });
      window.addEventListener('resize', updatePopupPosition, { passive: true });
      document.addEventListener('scroll', updatePopupPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updatePopupPosition);
        window.removeEventListener('resize', updatePopupPosition);
        document.removeEventListener('scroll', updatePopupPosition, { capture: true });
      };
    }
  }, [showPopup, updatePopupPosition]);

  const getGuestSummary = () => {
    const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
    const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);
    const totalRooms = rooms.length;
    
    if (totalChildren > 0) {
      return `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}, ${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}, ${totalRooms} Room${totalRooms > 1 ? 's' : ''}`;
    }
    return `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}, ${totalRooms} Room${totalRooms > 1 ? 's' : ''}`;
  };

  const updateRoomCount = (newCount) => {
    const count = Math.max(1, Math.min(6, newCount));
    const newRooms = [...rooms];
    
    if (count > newRooms.length) {
      for (let i = newRooms.length; i < count; i++) {
        newRooms.push({
          roomNumber: i + 1,
          adults: 2,
          children: 0,
          childAges: []
        });
      }
    } else if (count < newRooms.length) {
      newRooms.splice(count);
    }
    
    newRooms.forEach((room, index) => {
      room.roomNumber = index + 1;
    });
    
    onRoomsChange(newRooms);
  };

  const updateRoom = (roomIndex, field, value) => {
    const newRooms = [...rooms];
    
    if (field === 'adults') {
      newRooms[roomIndex].adults = Math.max(1, Math.min(8, value));
    } else if (field === 'children') {
      const childCount = Math.max(0, Math.min(4, value));
      newRooms[roomIndex].children = childCount;
      
      const childAges = [...(newRooms[roomIndex].childAges || [])];
      if (childCount > childAges.length) {
        for (let i = childAges.length; i < childCount; i++) {
          childAges.push(1);
        }
      } else if (childCount < childAges.length) {
        childAges.splice(childCount);
      }
      newRooms[roomIndex].childAges = childAges;
    }
    
    onRoomsChange(newRooms);
  };

  const updateChildAge = (roomIndex, childIndex, age) => {
    const newRooms = [...rooms];
    if (!newRooms[roomIndex].childAges) {
      newRooms[roomIndex].childAges = [];
    }
    newRooms[roomIndex].childAges[childIndex] = parseInt(age);
    onRoomsChange(newRooms);
  };

  const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
  const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);

  return (
    <div className="relative" style={{ zIndex: 9999 }}>
      {/* Input Trigger */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <input
          ref={guestInputRef}
          type="text"
          value={getGuestSummary()}
          onClick={() => setShowPopup(true)}
          readOnly
          title={getGuestSummary()}
          className={`w-full pl-10 pr-9 py-3 text-sm border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 ${theme.focusRing} bg-white hover:border-gray-400 transition-colors duration-200 shadow-sm font-medium text-gray-800 truncate h-[46px] select-none`}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showPopup ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Luxury Guests & Rooms Dropdown via React Portal to avoid containing-block clipping */}
      {showPopup && createPortal(
        <>
          {/* Backdrop overlay for outside clicks */}
          <div
            className="fixed inset-0 z-[9998] bg-transparent"
            onClick={() => setShowPopup(false)}
          />

          <div 
            className="guest-selector-popup fixed bg-white border border-gray-200/90 rounded-2xl shadow-2xl p-4 sm:p-5 transition-all z-[9999]"
            style={{ 
              top: `${popupPos.top}px`,
              left: `${popupPos.left}px`,
              width: `${popupPos.width}px`,
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                  <svg className={`w-4 h-4 ${theme.textAccent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Guests & Rooms
                </h3>
                <p className="text-[11px] text-gray-500 font-medium">
                  {rooms.length} {rooms.length === 1 ? 'Room' : 'Rooms'} · {totalAdults + totalChildren} {totalAdults + totalChildren === 1 ? 'Guest' : 'Guests'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="text-gray-400 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Rooms Scrollable List */}
            <div 
              className="flex-1 overflow-y-auto py-3 space-y-3 pr-1"
              style={{ 
                maxHeight: '360px',
                scrollbarWidth: 'thin'
              }}
            >
              {rooms.map((room, roomIndex) => (
                <div 
                  key={roomIndex} 
                  className={`${theme.cardBg} rounded-xl p-3.5 border shadow-xs transition-all`}
                >
                  {/* Room Header */}
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200/70">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${theme.roomBadge} border`}>
                        Room {room.roomNumber}
                      </span>
                      <span className="text-[11px] text-gray-500 font-medium">
                        {room.adults} {room.adults === 1 ? 'Adult' : 'Adults'}{room.children > 0 ? `, ${room.children} ${room.children === 1 ? 'Child' : 'Children'}` : ''}
                      </span>
                    </div>
                    {rooms.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newRooms = rooms.filter((_, i) => i !== roomIndex);
                          newRooms.forEach((r, i) => r.roomNumber = i + 1);
                          onRoomsChange(newRooms);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-0.5 rounded-md hover:bg-red-50 flex items-center gap-1 transition-colors"
                        title="Remove this room"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Steppers */}
                  <div className="space-y-3">
                    {/* Adults Row */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-gray-900">Adults</div>
                        <div className="text-[11px] text-gray-500 font-medium">Ages 13+</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateRoom(roomIndex, 'adults', room.adults - 1)}
                          className={`w-7 h-7 bg-white border border-gray-300 rounded-full ${theme.stepperHover} flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none shadow-xs transition-all`}
                          disabled={room.adults <= 1}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-6 text-center text-sm font-bold text-gray-900 select-none">
                          {room.adults}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateRoom(roomIndex, 'adults', room.adults + 1)}
                          className={`w-7 h-7 bg-white border border-gray-300 rounded-full ${theme.stepperHover} flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none shadow-xs transition-all`}
                          disabled={room.adults >= 8}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Children Row */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-gray-900">Children</div>
                        <div className="text-[11px] text-gray-500 font-medium">Ages 0 to 12</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateRoom(roomIndex, 'children', room.children - 1)}
                          className={`w-7 h-7 bg-white border border-gray-300 rounded-full ${theme.stepperHover} flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none shadow-xs transition-all`}
                          disabled={room.children <= 0}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-6 text-center text-sm font-bold text-gray-900 select-none">
                          {room.children}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateRoom(roomIndex, 'children', room.children + 1)}
                          className={`w-7 h-7 bg-white border border-gray-300 rounded-full ${theme.stepperHover} flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none shadow-xs transition-all`}
                          disabled={room.children >= 4}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Child Ages Dropdowns */}
                  {room.children > 0 && (
                    <div className="mt-3 pt-2.5 border-t border-gray-200/70 bg-white/70 rounded-lg p-2.5">
                      <div className="text-[11px] font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Child Age at check-in</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Array.from({ length: room.children }, (_, childIndex) => (
                          <div key={childIndex} className="flex items-center space-x-1.5 bg-white border border-gray-200 rounded-lg px-2 py-1 shadow-xs">
                            <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">Child {childIndex + 1}:</span>
                            <select
                              value={room.childAges?.[childIndex] || 1}
                              onChange={(e) => updateChildAge(roomIndex, childIndex, e.target.value)}
                              className={`w-full text-xs font-semibold text-gray-800 bg-transparent focus:outline-none ${theme.selectFocus} cursor-pointer`}
                            >
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(age => (
                                <option key={age} value={age}>{age} {age === 1 ? 'yr' : 'yrs'}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Another Room Button */}
              {rooms.length < 6 && (
                <button
                  type="button"
                  onClick={() => updateRoomCount(rooms.length + 1)}
                  className={`w-full py-2.5 px-3 border-2 border-dashed ${theme.addBtn} rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5 group shadow-xs`}
                >
                  <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Another Room ({rooms.length}/6)</span>
                </button>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2.5">
              <button
                type="button"
                onClick={() => {
                  onRoomsChange([{ roomNumber: 1, adults: 2, children: 0, childAges: [] }]);
                }}
                className="text-xs font-medium text-gray-500 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className={`flex-1 bg-gradient-to-r ${theme.btnBg} text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5 active:scale-[0.98]`}
              >
                <span>Apply Selection</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default GuestSelector;